import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Image as ImageIcon, 
  Upload,
  X,
  Info,
  Download,
  Loader2
} from 'lucide-react';
import { ImageMetadata } from '../../types/image-preview';
import { extractImageMetadata, formatDimensions } from '../../utils/imageMetadata';

interface ImageComparisonViewProps {
  // 原图相关
  originalFile: File | null;
  onFileChange: (file: File | null) => void;
  
  // 处理结果相关
  processedResult: any;
  isProcessing: boolean;
  
  // 配置
  supportedFormats: string[];
  
  // 回调
  onImageLoad?: (metadata: ImageMetadata) => void;
  onError?: (error: string) => void;
  onDownload?: () => void;
}

interface ViewState {
  originalImageUrl: string | null;
  originalMetadata: ImageMetadata | null;
  processedImageUrl: string | null;
  zoom: number;
  showMetadata: boolean;
  isLoadingOriginal: boolean;
  error: string | null;
}

export const ImageComparisonView: React.FC<ImageComparisonViewProps> = ({
  originalFile,
  onFileChange,
  processedResult,
  isProcessing,
  supportedFormats,
  onImageLoad,
  onError,
  onDownload
}) => {
  const [state, setState] = useState<ViewState>({
    originalImageUrl: null,
    originalMetadata: null,
    processedImageUrl: null,
    zoom: 1,
    showMetadata: true,
    isLoadingOriginal: false,
    error: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUrlRef = useRef<string | null>(null);

  // 处理原图文件变更
  useEffect(() => {
    // 清理之前的URL
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }

    if (!originalFile) {
      setState(prev => ({
        ...prev,
        originalImageUrl: null,
        originalMetadata: null,
        error: null
      }));
      return;
    }

    // 验证文件类型
    if (!originalFile.type.startsWith('image/')) {
      setState(prev => ({
        ...prev,
        error: '只支持图片文件',
        isLoadingOriginal: false
      }));
      onError?.('只支持图片文件');
      return;
    }

    setState(prev => ({ ...prev, isLoadingOriginal: true, error: null }));

    // 生成预览URL
    const url = URL.createObjectURL(originalFile);
    currentUrlRef.current = url;
    
    // 提取元数据
    extractImageMetadata(originalFile)
      .then(metadata => {
        if (currentUrlRef.current === url) {
          setState(prev => ({
            ...prev,
            originalImageUrl: url,
            originalMetadata: metadata,
            isLoadingOriginal: false,
            error: null
          }));
          onImageLoad?.(metadata);
        } else {
          URL.revokeObjectURL(url);
        }
      })
      .catch(error => {
        if (currentUrlRef.current === url) {
          setState(prev => ({
            ...prev,
            error: error.message,
            isLoadingOriginal: false
          }));
          onError?.(error.message);
        }
        URL.revokeObjectURL(url);
        if (currentUrlRef.current === url) {
          currentUrlRef.current = null;
        }
      });

    return () => {
      if (currentUrlRef.current === url) {
        URL.revokeObjectURL(url);
        currentUrlRef.current = null;
      }
    };
  }, [originalFile, onImageLoad, onError]);

  // 处理处理结果变更
  useEffect(() => {
    if (processedResult?.processed_data) {
      try {
        let url: string;
        if (processedResult.processed_data.startsWith('data:image/')) {
          url = processedResult.processed_data;
        } else {
          url = `data:image/${processedResult.output_format || 'jpeg'};base64,${processedResult.processed_data}`;
        }
        setState(prev => ({ ...prev, processedImageUrl: url }));
      } catch (error) {
        console.error('生成处理结果预览URL失败:', error);
        setState(prev => ({ ...prev, processedImageUrl: null }));
      }
    } else {
      setState(prev => ({ ...prev, processedImageUrl: null }));
    }
  }, [processedResult]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    };
  }, []);

  // 文件选择处理
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onFileChange(selectedFile);
    }
  }, [onFileChange]);

  // 拖拽处理
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      onFileChange(droppedFile);
    }
  }, [onFileChange]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  // 移除文件
  const handleRemoveFile = useCallback(() => {
    onFileChange(null);
  }, [onFileChange]);

  // 缩放控制
  const handleZoom = useCallback((action: 'in' | 'out' | 'reset') => {
    setState(prev => {
      let newZoom = prev.zoom;
      switch (action) {
        case 'in':
          newZoom = Math.min(prev.zoom * 1.2, 3);
          break;
        case 'out':
          newZoom = Math.max(prev.zoom / 1.2, 0.2);
          break;
        case 'reset':
          newZoom = 1;
          break;
      }
      return { ...prev, zoom: newZoom };
    });
  }, []);

  // 切换元数据显示
  const toggleMetadata = useCallback(() => {
    setState(prev => ({ ...prev, showMetadata: !prev.showMetadata }));
  }, []);

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 计算压缩率
  const compressionRatio = processedResult ? 
    ((processedResult.original_size - processedResult.processed_size) / processedResult.original_size) * 100 : 0;

  // 渲染工具栏
  const renderToolbar = () => (
    <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleZoom('out')}
          disabled={state.zoom <= 0.2}
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm text-gray-600 min-w-[50px] text-center">
          {Math.round(state.zoom * 100)}%
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleZoom('in')}
          disabled={state.zoom >= 3}
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleZoom('reset')}
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleMetadata}
        >
          <Info className="w-4 h-4 mr-1" />
          {state.showMetadata ? '隐藏信息' : '显示信息'}
        </Button>
        
        {processedResult?.success && onDownload && (
          <Button onClick={onDownload} size="sm">
            <Download className="w-4 h-4 mr-1" />
            下载
          </Button>
        )}
      </div>
    </div>
  );

  // 渲染原图区域
  const renderOriginalSection = () => (
    <div className="w-1/2 border-r">
      <div className="p-3 bg-blue-50 border-b">
        <h3 className="font-medium text-blue-900 flex items-center">
          <ImageIcon className="w-4 h-4 mr-2" />
          原图
        </h3>
      </div>
      
      <div className="relative" style={{ minHeight: '400px' }}>
        {!originalFile ? (
          // 上传区域
          <div
            className="h-full flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="text-center p-8">
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-600 mb-2">
                点击或拖拽上传图片
              </p>
              <p className="text-sm text-gray-500">
                {supportedFormats.length > 0
                  ? `支持格式: ${supportedFormats.join(', ').toUpperCase()}`
                  : '支持所有常见图片格式'
                }
              </p>
            </div>
          </div>
        ) : (
          // 图片显示区域
          <div className="relative h-full">
            {/* 文件信息条 */}
            <div className="absolute top-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ImageIcon className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium truncate max-w-[200px]">{originalFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(originalFile.size)} • {originalFile.type.split('/')[1].toUpperCase()}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* 图片内容 */}
            <div className="h-full flex items-center justify-center p-4 pt-16">
              {state.isLoadingOriginal ? (
                <div className="flex items-center text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  加载中...
                </div>
              ) : state.error ? (
                <div className="text-red-500 text-center">
                  <p>{state.error}</p>
                </div>
              ) : state.originalImageUrl ? (
                <img
                  src={state.originalImageUrl}
                  alt="原图"
                  style={{
                    transform: `scale(${state.zoom})`,
                    transformOrigin: 'center',
                    transition: 'transform 0.2s ease-in-out',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // 渲染处理结果区域
  const renderProcessedSection = () => (
    <div className="w-1/2">
      <div className="p-3 bg-green-50 border-b">
        <h3 className="font-medium text-green-900 flex items-center">
          <ImageIcon className="w-4 h-4 mr-2" />
          处理结果
          {isProcessing && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
        </h3>
      </div>
      
      <div className="relative" style={{ minHeight: '400px' }}>
        {!originalFile ? (
          // 等待上传提示
          <div className="h-full flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-400">
              <ImageIcon className="w-16 h-16 mx-auto mb-4" />
              <p>请先上传图片</p>
            </div>
          </div>
        ) : isProcessing ? (
          // 处理中状态
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-500" />
              <p className="text-gray-600">正在处理图片...</p>
            </div>
          </div>
        ) : !processedResult ? (
          // 等待处理提示
          <div className="h-full flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-400">
              <ImageIcon className="w-16 h-16 mx-auto mb-4" />
              <p>等待处理图片</p>
            </div>
          </div>
        ) : !processedResult.success ? (
          // 处理失败
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-red-500">
              <p>处理失败: {processedResult.error_message || '未知错误'}</p>
            </div>
          </div>
        ) : !state.processedImageUrl ? (
          // 无法显示结果
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-orange-500">
              <p>无法显示处理结果</p>
            </div>
          </div>
        ) : (
          // 处理结果显示
          <div className="relative h-full">
            {/* 结果信息条 */}
            <div className="absolute top-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 z-10">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-green-600 font-medium">
                    {formatFileSize(processedResult.processed_size)}
                  </span>
                  {compressionRatio > 0 && (
                    <span className="text-purple-600">
                      压缩 {compressionRatio.toFixed(1)}%
                    </span>
                  )}
                  <span className="text-gray-500">
                    {(processedResult.processing_time * 1000).toFixed(0)}ms
                  </span>
                </div>
                <span className="text-gray-600 text-xs">
                  {processedResult.output_format?.toUpperCase()}
                </span>
              </div>
            </div>

            {/* 处理后图片 */}
            <div className="h-full flex items-center justify-center p-4 pt-16">
              <img
                src={state.processedImageUrl}
                alt="处理后图片"
                style={{
                  transform: `scale(${state.zoom})`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // 渲染元数据面板
  const renderMetadataPanel = () => {
    if (!state.showMetadata || (!state.originalMetadata && !processedResult)) {
      return null;
    }

    return (
      <div className="border-t bg-gray-50 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 原图信息 */}
          {state.originalMetadata && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">原图信息</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">尺寸:</span>
                  <span>{formatDimensions(state.originalMetadata.width, state.originalMetadata.height)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">大小:</span>
                  <span>{formatFileSize(state.originalMetadata.size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">格式:</span>
                  <span>{state.originalMetadata.type.split('/')[1].toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">比例:</span>
                  <span>{state.originalMetadata.aspectRatio.toFixed(2)}:1</span>
                </div>
              </div>
            </div>
          )}

          {/* 处理结果信息 */}
          {processedResult?.success && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">处理信息</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">处理器:</span>
                  <span>{processedResult.processor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">处理后大小:</span>
                  <span>{formatFileSize(processedResult.processed_size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">输出格式:</span>
                  <span>{processedResult.output_format?.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">处理时间:</span>
                  <span>{(processedResult.processing_time * 1000).toFixed(0)}ms</span>
                </div>
                {compressionRatio > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">压缩率:</span>
                    <span className="text-green-600 font-medium">{compressionRatio.toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-0">
        <CardTitle className="flex items-center">
          <ImageIcon className="w-5 h-5 mr-2" />
          图片对比预览
        </CardTitle>
      </CardHeader>
      
      {renderToolbar()}
      
      <CardContent className="p-0">
        {state.error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700">{state.error}</p>
          </div>
        )}
        
        <div className="flex min-h-[400px]">
          {renderOriginalSection()}
          {renderProcessedSection()}
        </div>
      </CardContent>
      
      {renderMetadataPanel()}
    </Card>
  );
};

export default ImageComparisonView;